import { defer, LoaderFunctionArgs } from "react-router-dom";
import { getCachedOrFetch } from "@/queryClient";
import { fetchApi } from "@tiny/utils";
import type { GetSenderDetailsResponse } from "@/types/api/senderDetails";
import type {
  GetAccountSubscriptionResponse,
  GetSameSubscriptionResponse,
} from "@/types/api/accountSubscription";
import type { GetNotificationResponse } from "@/types/api/notifications";
import type { AuthStatusReponse } from "../AccountSettings/DNSAuthentication/DNSAuthentication.types";

export const mainLayoutKeys = {
  all: ["mainLayout"] as const,
  planInfo: () => [...mainLayoutKeys.all, "planInfo"] as const,
  senderDetails: () => [...mainLayoutKeys.all, "senderDetails"] as const,
  notifications: () => [...mainLayoutKeys.all, "notifications"] as const,
  subscription: () => [...mainLayoutKeys.all, "subscription"] as const,
  domainValidations: () =>
    [...mainLayoutKeys.all, "domainValidations"] as const,
};

const mainLayoutQueryFns = {
  planInfo: (signal?: AbortSignal) =>
    fetchApi<GetAccountSubscriptionResponse>(
      "/accountservice/account-subscription",
      { signal }
    ),

  senderDetails: (signal?: AbortSignal) =>
    fetchApi<GetSenderDetailsResponse>(
      "/accountservice/sender-details/default",
      { signal }
    ),

  notifications: (signal?: AbortSignal) =>
    fetchApi<GetNotificationResponse>(
      "/accountservice/account/issue/unresolved",
      { signal }
    ),

  subscription: (signal?: AbortSignal) =>
    fetchApi<GetSameSubscriptionResponse>(
      "/accountservice/subscriptions/update-check",
      { signal }
    ),

  domainValidations: (email: string, signal?: AbortSignal) =>
    fetchApi<AuthStatusReponse>(
      `/notificationservice/v0/email-auth/validate?email=${email}`,
      {
        method: "GET",
        signal,
      }
    ),
};

const mainLayoutQueryConfigs = {
  planInfo: {
    queryKey: mainLayoutKeys.planInfo(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      mainLayoutQueryFns.planInfo(signal),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  },

  senderDetails: {
    queryKey: mainLayoutKeys.senderDetails(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      mainLayoutQueryFns.senderDetails(signal),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  },

  notifications: {
    queryKey: mainLayoutKeys.notifications(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      mainLayoutQueryFns.notifications(signal),
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  },

  subscription: {
    queryKey: mainLayoutKeys.subscription(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      mainLayoutQueryFns.subscription(signal),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  },

  domainValidations: (email: string) => ({
    queryKey: mainLayoutKeys.domainValidations(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      mainLayoutQueryFns.domainValidations(email, signal),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  }),
};

export async function mainLayoutLoader({ request }: LoaderFunctionArgs) {
  const { signal } = request;

  console.log("main loader called");

  const [planInfo, senderDetails, notifications, subscriptionResponse] =
    await Promise.all([
      getCachedOrFetch(mainLayoutQueryConfigs.planInfo, signal),
      getCachedOrFetch(mainLayoutQueryConfigs.senderDetails, signal),
      getCachedOrFetch(mainLayoutQueryConfigs.notifications, signal),
      getCachedOrFetch(mainLayoutQueryConfigs.subscription, signal),
    ]);

  let domainValidations = {};
  try {
    if (senderDetails?.email) {
      domainValidations = await getCachedOrFetch(
        mainLayoutQueryConfigs.domainValidations(senderDetails.email),
        signal
      );
    }
  } catch (dkimError) {
    console.error("Failed to fetch DKIM status:", dkimError);
  }

  return defer({
    planInfo,
    notifications,
    senderDetails: {
      ...senderDetails,
      ...domainValidations,
    },
    subscription: subscriptionResponse?.subscriptions?.[0],
  });
}
